<template>
    <div class="mapbox-overlay">
        <div class="mapbox-overlay__panel">
            <div class="mapbox-overlay__layers-toggle" @click="onToggleClicked">
                {{
                    $t('installationoverview.map_layers', {
                        i18nFallback: 'Layers',
                    })
                }}

                <span>
                    <span v-if="state" class="sg-vrm-icon_125_arrow-up-16px"></span>
                    <span v-else class="sg-vrm-icon_124_arrow-down-16px"></span>
                </span>
            </div>
        </div>
        <div v-if="state" class="mapbox-overlay__panel mapbox__layers">
            <div class="mapbox-overlay__layers__title">
                {{
                    $t('installationoverview.generator', {
                        i18nFallback: 'Generator',
                    })
                }}
            </div>

            <div class="mapbox-overlay__layers__control" @click.prevent="onRuntimeClicked">
                <vrm-checkbox input-id="runtime" :model="runtimeUntilService"></vrm-checkbox>

                <label for="runtime">
                    {{
                        $t('installationoverview.serviceTime', {
                            i18nFallback: 'Runtime until service',
                        })
                    }}
                </label>

                <div class="map-stat__icon service"></div>
            </div>

            <div class="mapbox-overlay__layers__control" @click.prevent="onTankClicked">
                <vrm-checkbox input-id="tank-level" :model="tankLevel"></vrm-checkbox>

                <label for="tank-level">
                    {{
                        $t('installationoverview.map_label_fuel_tank_level', {
                            i18nFallback: 'Fuel tank level',
                        })
                    }}
                </label>

                <div class="map-stat__icon tank"></div>
            </div>

            <hr />

            <div class="mapbox-overlay__layers__title markers">
                {{
                    $t('installationoverview.layer_title_map_markers', {
                        i18nFallback: 'Map Markers',
                    })
                }}
            </div>

            <div class="mapbox-overlay__layers__control" @click.prevent="onOkToggled">
                <vrm-checkbox input-id="show-ok" :model="showOk"></vrm-checkbox>

                <label for="runtime">
                    {{
                        $t('installationoverview.layer_status_ok', {
                            i18nFallback: 'Status OK',
                        })
                    }}
                </label>

                <div class="icon">
                    <img src="/img/svg-icons/marker-individual-ok.svg" />
                </div>
            </div>

            <div class="mapbox-overlay__layers__control" @click.prevent="onWarningsToggled">
                <vrm-checkbox input-id="show-warnings" :model="showWarnings"></vrm-checkbox>

                <label for="tank-level">
                    {{
                        $t('installationoverview.layer_status_warning', {
                            i18nFallback: 'Status Warning',
                        })
                    }}
                </label>

                <div class="icon">
                    <img src="/img/svg-icons/marker-individual-warning.svg" />
                </div>
            </div>

            <div class="mapbox-overlay__layers__control" @click.prevent="onAlarmsToggled">
                <vrm-checkbox input-id="show-alarms" :model="showAlarms"></vrm-checkbox>

                <label for="tank-level">
                    {{
                        $t('installationoverview.layer_status_alarm', {
                            i18nFallback: 'Status Alarm',
                        })
                    }}
                </label>

                <div class="icon">
                    <img src="/img/svg-icons/marker-individual-error.svg" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import VrmControlledToggle from '@/vue/components/shared/toggles/VrmControlledToggle.vue';
import VrmToggle from '@/vue/components/shared/toggles/VrmToggle.vue';
import VrmCheckbox from '@/vue/components/forms/VrmCheckbox.vue';

export default defineComponent({
    name: 'VrmMapLayerControls',
    components: { VrmCheckbox, VrmToggle, VrmControlledToggle },
    props: {
        layers: {
            type: Object,
            required: true,
            default: () => {
                return {
                    runtime: true,
                    tank: true,
                    showOk: true,
                    showWarnings: true,
                    showAlarms: true,
                };
            },
        },
        /**
         * Initial open state for the control panel.
         */
        initialState: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    emits: [
        'layers-changed',
        'runtime-changed',
        'tank-changed',
        'show-ok-changed',
        'show-warnings-changed',
        'show-alarms-changed',
    ],
    data() {
        // Use non-reactive data props to prevent render loops.
        return {
            runtimeUntilService: null,
            tankLevel: null,
            state: false,
            showOk: true,
            showWarnings: true,
            showAlarms: true,
        };
    },
    created() {
        this.state = this.initialState;

        this.updateLayerValues();
    },
    watch: {
        /**
         * Listen for layer prop to change and update the internal state.
         *
         * @param next
         */
        // eslint-disable-next-line no-unused-vars
        layers(next) {
            this.updateLayerValues();
        },

        /**
         * Emit an update event whenever the runtime value changes.
         *
         * @param next
         */
        // eslint-disable-next-line no-unused-vars
        runtimeUntilService(next) {
            this.$emit('runtime-changed', this.runtimeUntilService);
            this.$emit('layers-changed', this.getLayers());
        },
        /**
         * Emit an update event whenever the tank value changes.
         *
         * @param next
         */
        // eslint-disable-next-line no-unused-vars
        tankLevel(next) {
            this.$emit('tank-changed', this.tankLevel);
            this.$emit('layers-changed', this.getLayers());
        },
        /**
         * Emit an update event whenever the showOk value changes.
         *
         * @param next
         */
        // eslint-disable-next-line no-unused-vars
        showOk(next) {
            this.$emit('show-ok-changed', this.showOk);
            this.$emit('layers-changed', this.getLayers());
        },
        /**
         * Emit an update event whenever the showWarnings value changes.
         *
         * @param next
         */
        // eslint-disable-next-line no-unused-vars
        showWarnings(next) {
            this.$emit('show-warnings-changed', this.showWarnings);
            this.$emit('layers-changed', this.getLayers());
        },
        /**
         * Emit an update event whenever the showAlarms value changes.
         *
         * @param next
         */
        // eslint-disable-next-line no-unused-vars
        showAlarms(next) {
            this.$emit('show-alarms-changed', this.showAlarms);
            this.$emit('layers-changed', this.getLayers());
        },
    },
    methods: {
        updateLayerValues() {
            this.runtimeUntilService = this.layers.runtimeUntilService;
            this.tankLevel = this.layers.tank;

            this.showOk = this.layers.showOk;
            this.showWarnings = this.layers.showWarnings;
            this.showAlarms = this.layers.showAlarms;
        },
        /**
         * Get an object containing all current layer values.
         *
         * @returns {{runtime: Boolean, tank: Boolean}}
         */
        getLayers() {
            return {
                runtimeUntilService: this.runtimeUntilService,
                tank: this.tankLevel,
                showOk: this.showOk,
                showWarnings: this.showWarnings,
                showAlarms: this.showAlarms,
            };
        },
        /**
         * Toggle the control panel state
         */
        onToggleClicked() {
            this.state = !this.state;
        },

        // eslint-disable-next-line no-unused-vars
        onRuntimeClicked(e) {
            this.runtimeUntilService = !this.runtimeUntilService;
        },
        // eslint-disable-next-line no-unused-vars
        onTankClicked(e) {
            this.tankLevel = !this.tankLevel;
        },
        // eslint-disable-next-line no-unused-vars
        onOkToggled(e) {
            this.showOk = !this.showOk;
        },
        // eslint-disable-next-line no-unused-vars
        onWarningsToggled(e) {
            this.showWarnings = !this.showWarnings;
        },
        // eslint-disable-next-line no-unused-vars
        onAlarmsToggled(e) {
            this.showAlarms = !this.showAlarms;
        },
    },
});
</script>
